import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { formatFileSize } from '@src/services/Helper'
import { rejectReasonText, trayReasonText } from '../constants'

// Long lists show this many lines until HR asks for all of them.
const SHOWN = 20

function Section({ title, tone, count, action, children }) {
  const border = tone === 'error' ? 'border-error/200' : 'border-warning/200'
  const bg = tone === 'error' ? 'bg-error/25' : 'bg-warning/25'
  return (
    <section className={`flex flex-col rounded-xl border ${border} ${bg}`} aria-label={title}>
      <div className="flex items-center justify-between gap-3 px-4 py-2.5">
        <p className="text-sm font-semibold text-gray-900">
          {title} <span className="font-normal text-gray-500">({count})</span>
        </p>
        {action}
      </div>
      <ul className="flex flex-col divide-y divide-gray-100 border-t border-gray-100 bg-white">{children}</ul>
    </section>
  )
}
Section.propTypes = {
  title: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(['warning', 'error']).isRequired,
  count: PropTypes.number.isRequired,
  action: PropTypes.node,
  children: PropTypes.node,
}

const linkButton = 'text-xs font-semibold text-brand/700 hover:underline disabled:text-gray-400 disabled:no-underline'

function FileLine({ entry, reason, children }) {
  return (
    <li className="flex flex-col gap-1 px-4 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium text-gray-900" title={entry.file.name}>
          {entry.file.name}
          <span className="ml-2 text-xs font-normal text-gray-500">{formatFileSize(entry.file.size || 0)}</span>
        </span>
        <span className="text-xs text-gray-600">{reason}</span>
      </div>
      {children && <div className="flex shrink-0 gap-3">{children}</div>}
    </li>
  )
}
FileLine.propTypes = { entry: PropTypes.object.isRequired, reason: PropTypes.node, children: PropTypes.node }

function useShowAll(list) {
  const [all, setAll] = useState(false)
  const shown = all ? list : list.slice(0, SHOWN)
  const more = list.length - shown.length
  const toggle =
    list.length > SHOWN ? (
      <li className="px-4 py-2">
        <button type="button" className={linkButton} onClick={() => setAll((v) => !v)}>
          {all ? 'Show fewer' : `Show ${more} more`}
        </button>
      </li>
    ) : null
  return { shown, toggle }
}

/**
 * Files that did not go to a row by themselves ("Needs your choice"): the
 * reason, a preview, and Assign (pick the row and the file kind) or Dismiss.
 */
export default function UnmatchedTray({ entries, keyMode, disabled, onAssign, onPreview, onDismiss, onDismissAll }) {
  const { shown, toggle } = useShowAll(entries)
  if (!entries.length) return null
  return (
    <Section
      title="Needs your choice"
      tone="warning"
      count={entries.length}
      action={
        <button type="button" className={linkButton} onClick={onDismissAll} disabled={disabled}>
          Dismiss all
        </button>
      }
    >
      {shown.map((entry) => (
        <FileLine key={entry.id} entry={entry} reason={trayReasonText(entry, { keyMode })}>
          <button
            type="button"
            className={linkButton}
            onClick={() => onPreview(entry)}
            aria-label={`Preview ${entry.file.name}`}
          >
            Preview
          </button>
          <button
            type="button"
            className={linkButton}
            onClick={() => onAssign(entry)}
            disabled={disabled}
            aria-label={`Assign ${entry.file.name}`}
          >
            Assign
          </button>
          <button
            type="button"
            className={linkButton}
            onClick={() => onDismiss(entry.id)}
            disabled={disabled}
            aria-label={`Dismiss ${entry.file.name}`}
          >
            Dismiss
          </button>
        </FileLine>
      ))}
      {toggle}
    </Section>
  )
}

UnmatchedTray.propTypes = {
  entries: PropTypes.arrayOf(PropTypes.object).isRequired,
  keyMode: PropTypes.oneOf(['nik', 'email']).isRequired,
  disabled: PropTypes.bool,
  onAssign: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onDismissAll: PropTypes.func.isRequired,
}

/** Files that cannot be used in this batch at all ("Not usable"), with the reason. */
export function RejectedFiles({ entries, onDismissAll }) {
  const { shown, toggle } = useShowAll(entries)
  if (!entries.length) return null
  return (
    <Section
      title="Not usable"
      tone="error"
      count={entries.length}
      action={
        <button type="button" className={linkButton} onClick={onDismissAll}>
          Dismiss all
        </button>
      }
    >
      {shown.map((entry) => (
        <FileLine key={entry.id} entry={entry} reason={rejectReasonText(entry)} />
      ))}
      {toggle}
    </Section>
  )
}

RejectedFiles.propTypes = {
  entries: PropTypes.arrayOf(PropTypes.object).isRequired,
  onDismissAll: PropTypes.func.isRequired,
}
